import { useCallback, useEffect, useState } from "react";

export interface PackageItem {
  type: string;
  name: string;
  data: unknown;
}

export interface PackageSummary {
  id: string;
  name: string;
  description: string;
  itemCount: number;
  types: string;
  sourcePortalId: string;
  sourcePortalNickname: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  archivedAt: number | null;
}

export interface PackageFull extends PackageSummary {
  contents: PackageItem[];
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export function usePackages() {
  const [active, setActive] = useState<PackageSummary[]>([]);
  const [archived, setArchived] = useState<PackageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchJson<PackageSummary[]>(
        "/api/packages?includeArchived=true",
      );
      setActive(all.filter((p) => p.archivedAt === null));
      setArchived(all.filter((p) => p.archivedAt !== null));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const update = useCallback(
    async (id: string, patch: { name?: string; description?: string }) => {
      await fetchJson(`/api/packages/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      await reload();
    },
    [reload],
  );

  const archive = useCallback(
    async (id: string) => {
      await fetchJson(`/api/packages/${id}`, { method: "DELETE" });
      await reload();
    },
    [reload],
  );

  const restore = useCallback(
    async (id: string) => {
      await fetchJson(`/api/packages/${id}/restore`, { method: "POST" });
      await reload();
    },
    [reload],
  );

  const hardDelete = useCallback(
    async (id: string) => {
      await fetchJson(`/api/packages/${id}?hard=true`, { method: "DELETE" });
      await reload();
    },
    [reload],
  );

  return {
    active,
    archived,
    loading,
    error,
    reload,
    update,
    archive,
    restore,
    hardDelete,
  };
}
